import { useState, useEffect, useRef } from "react";
import { Icons } from "../../constants/icon-library";
import { formatPlatformName } from "../../constants/constants";
import { useDispatch } from "react-redux";
import { upsertParticipant } from "../../features/participant/participantSlice";
import { toast } from "react-toastify";
import {
  CREATE_PARTICIPANT_INITIAL_VALUES,
  createParticipantValidationSchema,
} from "../../constants/participant.constant";
import { useFormik } from "formik";
import MultiSelect from "../../components/common/MultiSelect";

const ParticipantsModal = ({
  show,
  onHide,
  participants = [],
  allParticipants = [],
  onSave,
  settings,
}) => {
  const dispatch = useDispatch();
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCreateRow, setShowCreateRow] = useState(false);

  const uniquePlatforms = [
    ...new Map(
      (settings || []).map((item) => [
        item?.assistantId?.platform?.uuid,
        {
          label: item?.assistantId?.platform?.name,
          value: item?.assistantId?.platform?.type,
        },
      ]),
    ).values(),
  ];

  const formik = useFormik({
    initialValues: CREATE_PARTICIPANT_INITIAL_VALUES,
    validationSchema: createParticipantValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await dispatch(upsertParticipant(values));

        if (res.payload?.uuid) {
          setSelectedParticipants((prev) => [...prev, res.payload]);

          toast.success("Participant created and added to meeting");

          resetForm();
          setShowCreateRow(false);
        } else {
          toast.error(res.payload?.message || "Something went wrong");
        }
      } catch (err) {
        toast.error(err?.message || "Failed to create participant");
      }
    },
  });

  const dropdownRef = useRef();

  useEffect(() => {
    if (show) {
      setSelectedParticipants(Array.isArray(participants) ? participants : []);
    }
  }, [participants, show]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!show) return null;

  const isSelected = (uuid) =>
    selectedParticipants.some((p) => p.uuid === uuid);

  const handleAdd = (participant) => {
    if (isSelected(participant.uuid)) return;
    setSelectedParticipants((prev) => [...prev, participant]);
    setDropdownOpen(false);
  };

  const handleRemove = (uuid) => {
    setSelectedParticipants((prev) => prev.filter((p) => p.uuid !== uuid));
  };

  const handleSave = () => {
    const participantUUIDs = selectedParticipants.map((p) => p.uuid);
    onSave && onSave(participantUUIDs);
    onHide();
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onHide} />

      <div className="modal fade show d-block">
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "720px" }}
        >
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <h5 className="fw-semibold">
                Participants ({selectedParticipants.length})
              </h5>
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-close p-1"
                  type="button"
                  aria-label="Close"
                  onClick={onHide}
                />
              </div>
            </div>

            <div
              className="px-3 pt-3 d-flex justify-content-end gap-2"
              ref={dropdownRef}
            >
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateRow((prev) => !prev)}
              >
                + New Participant
              </button>

              <button
                className="btn btn-primary btn-sm"
                onClick={() => setDropdownOpen((prev) => !prev)}
              >
                + Add Participant
              </button>

              {dropdownOpen && (
                <div
                  className="dropdown-menu show mt-2 shadow-sm"
                  style={{ minWidth: 280, maxHeight: 240, overflowY: "auto" }}
                >
                  {allParticipants.length > 0 ? (
                    allParticipants.map((p) => {
                      const disabled = isSelected(p.uuid);

                      return (
                        <button
                          key={p.uuid}
                          className={`dropdown-item d-flex justify-content-between ${
                            disabled ? "text-muted" : ""
                          }`}
                          onClick={() => handleAdd(p)}
                          disabled={disabled}
                        >
                          <span>
                            {p.name} ({p.username})
                          </span>
                          <small>{formatPlatformName(p.platforms)}</small>
                        </button>
                      );
                    })
                  ) : (
                    <span className="dropdown-item text-muted">
                      No participants
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="modal-body pt-2">
              <div className="table-responsive" style={{ maxHeight: 260 }}>
                <table className="table table-striped fs-9 mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Platforms</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {showCreateRow && (
                      <tr>
                        <td>
                          <input
                            name="name"
                            className="form-control form-control-sm"
                            placeholder="Name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />

                          {formik.touched.name && formik.errors.name && (
                            <small className="text-danger">
                              {formik.errors.name}
                            </small>
                          )}
                        </td>

                        <td>
                          <input
                            name="username"
                            className="form-control form-control-sm"
                            placeholder="Email / Username"
                            value={formik.values.username}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />

                          {formik.touched.username &&
                            formik.errors.username && (
                              <small className="text-danger">
                                {formik.errors.username}
                              </small>
                            )}
                        </td>

                        <td style={{ minWidth: 220 }}>
                          <MultiSelect
                            options={uniquePlatforms}
                            value={formik.values.platforms || []}
                            onChange={(value) =>
                              formik.setFieldValue("platforms", value)
                            }
                            placeholder="Select platform..."
                          />

                          {formik.touched.platforms &&
                            formik.errors.platforms && (
                              <small className="text-danger">
                                {formik.errors.platforms}
                              </small>
                            )}
                        </td>

                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={formik.handleSubmit}
                            >
                              <i className={`wb ${Icons("correct")}`} />
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => {
                                setShowCreateRow(false);
                                formik.resetForm();
                              }}
                            >
                              <i className={`wb ${Icons("cancel")}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {selectedParticipants.filter(Boolean).map((p) => (
                      <tr key={p.uuid}>
                        <td>{p.name || "-"}</td>

                        <td>{p.username || "-"}</td>

                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(p.platforms || []).length
                              ? p.platforms.map((platform, index) => (
                                  <span
                                    key={index}
                                    className="badge rounded-pill px-2 py-1 d-flex align-items-center gap-2"
                                    style={{
                                      background: "rgba(144, 255, 180, 0.15)",
                                      border:
                                        "1px solid rgba(144, 255, 180, 0.45)",
                                      color:
                                        "var(--phoenix-success-text-emphasis)",
                                      fontSize: "0.82rem",
                                    }}
                                  >
                                    {formatPlatformName(platform)}
                                  </span>
                                ))
                              : "-"}
                          </div>
                        </td>

                        <td className="text-center">
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemove(p.uuid)}
                          >
                            <i className={`wb ${Icons("delete")}`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedParticipants.length === 0 && !showCreateRow && (
                <p className="text-center text-muted mt-2">
                  No participants added.
                </p>
              )}
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={onHide}
              >
                Cancel
              </button>

              <button className="btn btn-primary btn-sm" onClick={handleSave}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParticipantsModal;
